import { z } from 'zod';

// Global error messages for validation
export const ERRORS = {
  unknown:
    'Something went wrong. Please wait a few seconds and then try again.',
  required: 'This field is required.',
  invalid_type: "That data doesn't look right. Check it and try again.",
  max: (value: number) => `It must be under ${value} characters.`,
  requiredOn: (value: string) =>
    `This field is required because "${value}" was selected.`,
};

export const customErrorMap: z.ZodErrorMap = (error, ctx) => {
  switch (error.code) {
    case z.ZodIssueCode.invalid_type:
      if (error.expected === 'string') {
        return { message: ERRORS.invalid_type };
      }
      break;
  }

  // fall back to default message
  return { message: ctx.defaultError };
};

// Set the custom error map globally
z.setErrorMap(customErrorMap);

// Common base schemas used across all entities
export const DateSchema = z.date().transform(value => value.toISOString());
export const StringSchema = z.string();
export const TrimmedStringSchema = StringSchema.trim();
export const NonEmptyStringSchema = StringSchema.min(1, ERRORS.required);
export const PositiveNumberSchema = z.number().int().min(0);

const isValidLanguageCode = (code: string) => {
  try {
    return Intl.getCanonicalLocales(code).length > 0;
  } catch {
    return false;
  }
};

export const LanguageCodeSchema = z
  .string()
  .refine(value => isValidLanguageCode(value), 'Invalid language code');

// Common field-level validators
export const RequiredStringSchema = z
  .string({
    required_error: ERRORS.required,
  })
  .trim()
  .min(1, ERRORS.required);

export const OptionalStringSchema = z.string().trim().optional();

export const MaxLengthStringSchema = (maxLength: number) =>
  z.string().trim().max(maxLength, ERRORS.max(maxLength));

export const RequiredMaxLengthStringSchema = (maxLength: number) =>
  z
    .string({
      required_error: ERRORS.required,
    })
    .trim()
    .min(1, ERRORS.required)
    .max(maxLength, ERRORS.max(maxLength));

export const OptionalMaxLengthStringSchema = (maxLength: number) =>
  z.string().trim().max(maxLength, ERRORS.max(maxLength)).optional();

export const BooleanSchema = z.boolean({
  required_error: ERRORS.required,
});

export const OptionalBooleanSchema = z.boolean().optional();

export const PositiveIntegerSchema = z.number().int().min(0);

export const NonNegativeIntegerSchema = z.number().int().min(0);

export const VariableNameSchema = z
  .string()
  .min(1)
  .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, 'Invalid value, name should be Pythonic');

export const IdSchema = NonEmptyStringSchema.max(100);
export const BaseEntitySchema = z.object({
  id: IdSchema,
});

export const createEntityAdapterSchema = <T extends z.ZodTypeAny>(schema: T) =>
  z.object({
    ids: z.array(IdSchema),
    entities: z.record(IdSchema, schema),
  });

export const ReferenceSchema = z.record(z.string(), z.boolean());

export const EnvironmentsSchema = z.enum(['sandbox', 'pre-release', 'live']);

// Common metadata fields for all entities
export const EntityMetadataSchema = z.object({
  createdAt: z.string(), // ISO string
  createdBy: z.string(),
  updatedAt: z.string(),
  updatedBy: z.string(),
});

// Common validation utilities
export const validateIdsAreUnique = (ids: Array<{ id: string }>) => {
  const idSet = new Set<string>();
  for (const id of ids) {
    if (idSet.has(id.id)) {
      return false;
    }
    idSet.add(id.id);
  }
  return true;
};

export const validateNamesAreUnique = (entities: Array<{ name: string }>) => {
  const names = new Set<string>();
  for (const entity of entities) {
    if (names.has(entity.name)) {
      return false;
    }
    names.add(entity.name);
  }
  return true;
};

export const validateNameIsUnique = (
  entities: Array<{ name: string; id: string }>,
  name: string,
  excludeId?: string,
) => {
  return !entities.some(
    entity => entity.name === name && entity.id !== excludeId,
  );
};

// Enhanced validation utilities that return detailed error information
export interface ValidationError {
  path: string;
  message: string;
  duplicateValue: string;
  duplicateIds: string[];
}

export const findDuplicateNames = <
  T extends { name?: string; id?: string; title?: string },
>(
  entities: Array<T>,
): Array<T> => {
  const nameMap = new Map<string, { seenTwice: boolean; originalEntity: T }>();
  const duplicates: T[] = [];

  for (const entity of entities) {
    const name = entity.name ?? entity.title ?? '';
    if (nameMap.has(name)) {
      duplicates.push(entity);
      const mapEntry = nameMap.get(name);
      if (!!mapEntry && !mapEntry.seenTwice) {
        duplicates.push(mapEntry.originalEntity);
        mapEntry.seenTwice = true;
      }
    } else {
      nameMap.set(name, { seenTwice: false, originalEntity: entity });
    }
  }

  return duplicates;
};

export const findDuplicateIds = <T extends { id: string }>(
  entities: Array<T>,
): Array<T> => {
  const idMap = new Map<string, { seenTwice: boolean; originalEntity: T }>();
  const duplicates: T[] = [];

  for (const entity of entities) {
    if (idMap.has(entity.id)) {
      duplicates.push(entity);
      const mapEntry = idMap.get(entity.id);
      if (!!mapEntry && !mapEntry.seenTwice) {
        duplicates.push(mapEntry.originalEntity);
        mapEntry.seenTwice = true;
      }
    } else {
      idMap.set(entity.id, { seenTwice: false, originalEntity: entity });
    }
  }

  return duplicates;
};
